import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useSearchParams } from "react-router";
import { authApi, useMe, type RegisterInput } from "@/shared/api/auth";
import { ApiError, errorMessage } from "@/shared/api/client";
import { useLegalLinks } from "@/shared/api/site";
import { Button } from "@/shared/ui/button";
import { CheckboxRow } from "@/shared/ui/checkbox";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { AuthLayout, AuthTitle, CodeStep, EmailField } from "@/core/auth-flow";
import { isEmail, safeNext } from "@/core/auth-utils";
import { projectPath, projectUi, type Answers } from "@/core/projects-ui";

/** Regisztráció — mindig **egy projektbe**. Ha a projektnek van belépő kérdése
 *  (`ProjectUi.gate`, a könyvnél „A könyv olvasója vagy?"), a folyamat azzal kezdődik: az
 *  „igen" viszi az űrlapra (és megválaszolja a projekt kérdését), a „nem" elmondja, kinek
 *  szól a melléklet, és visszavisz a projekt publikus oldalára. Az űrlapon a globális
 *  nyilatkozatok maradnak (rendszerterv 6.). */
export function RegisterPage() {
  const { data: me, isPending } = useMe();
  const [params] = useSearchParams();
  const legal = useLegalLinks();
  const slug = params.get("project") ?? "book";
  const ui = projectUi(slug);
  const next = safeNext(params.get("next")) || projectPath(slug);
  // a `/konyv?src=qr` linkje továbbadja: csak a névtelen napi számlálóba kerül (rendszerterv 9.1)
  const source = /^[a-z0-9-]{1,20}$/.test(params.get("src") ?? "") ? params.get("src")! : undefined;

  const [name, setName] = useState("");
  const [email, setEmail] = useState(params.get("email") ?? "");
  const [answers, setAnswers] = useState<Answers>({});
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exists, setExists] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);
  const gate = ui?.gate ?? null;
  const [step, setStep] = useState<"gate" | "denied" | "form" | "code">(
    gate ? "gate" : "form",
  );

  const register = useMutation({
    mutationFn: (input: RegisterInput) => authApi.register(input),
    onSuccess: (r) => {
      setDevCode(r.dev_code ?? null);
      setStep("code");
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === "already_registered") setExists(true);
      else setError(errorMessage(e));
    },
  });

  /** A „nem" válasz után magától visszaviszünk a publikus oldalra (a gomb is ott van). */
  useEffect(() => {
    if (step !== "denied" || !gate) return;
    const timer = setTimeout(() => window.location.replace(gate.deniedHref), 5000);
    return () => clearTimeout(timer);
  }, [step, gate]);

  if (!isPending && me) return <Navigate to={next} replace />;

  const loginHref = `/app/belepes?${new URLSearchParams({
    project: slug,
    ...(email ? { email } : {}),
  })}`;

  const submit = (ev: FormEvent) => {
    ev.preventDefault();
    setError(null);
    setExists(false);
    if (name.trim().length < 2) {
      return setError("Add meg, hogyan szólíthatunk (legalább 2 karakter).");
    }
    if (!isEmail(email.trim())) return setError("Adj meg egy érvényes e-mail címet.");
    if (ui && !ui.isValid(answers)) {
      return setError("A jelölt nyilatkozat elfogadása szükséges.");
    }
    if (!terms || !privacy) return setError("A jelölt nyilatkozatok elfogadása szükséges.");
    register.mutate({
      display_name: name.trim(),
      email: email.trim(),
      terms_accepted: terms,
      privacy_accepted: privacy,
      marketing_consent: marketing,
      project: slug,
      answers,
      ...(source ? { source } : {}),
    });
  };

  const loginLine = (
    <p className="text-[15px] text-muted">
      Van már fiókod?{" "}
      <Link to={loginHref} className="font-semibold text-gold-text">
        Belépek
      </Link>
    </p>
  );

  if (step === "gate" && gate) {
    return (
      <AuthLayout>
        <AuthTitle title={gate.question} />
        {/* A két válasz egy sorban, fele-fele szélességgel — így egyetlen pillantással
            látszik, hogy ez egy eldöntendő kérdés. */}
        <div className="flex gap-2.5">
          <Button
            size="lg"
            className="flex-1"
            onClick={() => {
              setAnswers({ ...answers, ...gate.answers });
              setStep("form");
            }}
          >
            {gate.yes}
          </Button>
          <Button
            size="lg"
            className="flex-1"
            variant="outlineInk"
            onClick={() => setStep("denied")}
          >
            {gate.no}
          </Button>
        </div>
        {loginLine}
      </AuthLayout>
    );
  }

  if (step === "denied" && gate) {
    return (
      <AuthLayout>
        <AuthTitle title={gate.deniedTitle}>{gate.deniedLead}</AuthTitle>
        <Button size="lg" block asChild>
          <a href={gate.deniedHref}>{gate.deniedCta}</a>
        </Button>
        <button
          type="button"
          onClick={() => setStep("gate")}
          className="text-[13px] font-semibold text-gold-text"
        >
          Mégis olvasom a könyvet
        </button>
      </AuthLayout>
    );
  }

  if (step === "code") {
    return (
      <AuthLayout>
        <CodeStep
          email={email.trim()}
          next={next}
          devCode={devCode}
          project={slug}
          onBack={() => setStep("form")}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form onSubmit={submit} noValidate className="flex flex-col gap-5">
        {/* A projekt neve a fejlécben már ott van — itt az áll, miért kérjük a regisztrációt. */}
        <AuthTitle title="Regisztráció">{ui?.registerLead}</AuthTitle>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="name">Hogyan szólíthatunk?</Label>
          <Input
            id="name"
            autoComplete="nickname"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Becenév vagy keresztnév"
            maxLength={80}
          />
          <p className="text-[12px] text-muted">
            Ezt a nevet látják a többiek, ha megosztasz valamit.
          </p>
        </div>

        <EmailField value={email} onChange={setEmail} autoFocus={false} />

        <div className="flex flex-col">
          {ui && !gate && <ui.Fields answers={answers} onChange={setAnswers} />}
          <CheckboxRow id="terms" checked={terms} onCheckedChange={setTerms} required>
            Elfogadom az{" "}
            <a
              href={legal.terms_url}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-gold-text"
            >
              ÁSZF
            </a>
            -et.
          </CheckboxRow>
          <CheckboxRow id="privacy" checked={privacy} onCheckedChange={setPrivacy} required>
            Megismertem az{" "}
            <a
              href={legal.privacy_url}
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-gold-text"
            >
              adatkezelési tájékoztatót
            </a>
            .
          </CheckboxRow>
          <CheckboxRow id="marketing" checked={marketing} onCheckedChange={setMarketing}>
            Megengeded, hogy e-mailben értesítsünk a jövőbeli AI-fejlesztéseinkről? (később
            bármikor visszavonhatod)
          </CheckboxRow>
        </div>

        {exists && (
          <div className="flex flex-col gap-2 rounded-lg border border-line bg-gray px-4 py-3">
            <p className="text-[14px] text-ink-soft">
              Ezzel az e-mail címmel már van fiókod.
            </p>
            <Link to={loginHref} className="text-[14px] font-semibold text-gold-text">
              Belépek →
            </Link>
          </div>
        )}
        {error && (
          <p className="text-[13px] text-red" role="alert">
            {error}
          </p>
        )}

        <Button type="submit" size="lg" block disabled={register.isPending}>
          {register.isPending ? "Küldés…" : "Regisztrálok és kódot kérek"}
        </Button>
        {loginLine}
      </form>
    </AuthLayout>
  );
}
